import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as L from 'leaflet';

@Component({
    selector: 'app-geolocation-map',
    standalone: true,
    imports: [CommonModule],
    encapsulation: ViewEncapsulation.None,
    template: `
        <div #mapContainer class="map-container" [style.height]="height"></div>
    `,
    styles: [`
        .map-container {
            width: 100%;
            min-height: 300px;
            max-height: 600px;
            border-radius: 8px;
            position: relative !important;
            overflow: hidden !important;
            z-index: 0 !important;
        }
        
        .leaflet-container {
            height: 100% !important;
            width: 100% !important;
            max-height: 100% !important;
            z-index: 0 !important;
        }
        
        .leaflet-pane {
            z-index: auto !important;
        }
        
        .leaflet-tile-pane {
            z-index: 200 !important;
            opacity: 1 !important;
        }
        
        .leaflet-tile {
            opacity: 1 !important;
        }
        
        .leaflet-marker-pane {
            z-index: 600 !important;
        }
    `]
})
export class GeolocationMapComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() latitude!: number;
    @Input() longitude!: number;
    @Input() city?: string;
    @Input() country?: string;
    @Input() zoom: number = 10;
    @Input() height: string = '300px';

    @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

    private map?: L.Map;
    private resizeObserver?: ResizeObserver;

    constructor(private cdr: ChangeDetectorRef) { }

    ngOnInit(): void {
        // Validate inputs
        if (this.latitude === undefined || this.longitude === undefined) {
            console.warn('GeolocationMapComponent: latitude and longitude are required');
        }
    }

    ngAfterViewInit(): void {
        // Initialize map after view is ready - multiple attempts to ensure rendering
        this.initMap();
        
        // Also try after a delay in case of slow rendering
        setTimeout(() => {
            if (!this.map) {
                console.log('Retrying map initialization...');
                this.initMap();
            }
        }, 100);
    }

    ngOnDestroy(): void {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.map) {
            this.map.remove();
        }
    }

    private initMap(): void {
        if (!this.mapContainer) {
            console.error('GeolocationMapComponent: mapContainer is not available');
            return;
        }

        if (!this.latitude || !this.longitude) {
            console.error('GeolocationMapComponent: latitude or longitude is missing', {
                latitude: this.latitude,
                longitude: this.longitude
            });
            return;
        }

        const container = this.mapContainer.nativeElement;
        
        // Check if map already exists
        if (this.map) {
            console.log('Map already initialized, skipping...');
            return;
        }

        console.log('Initializing map for:', { lat: this.latitude, lng: this.longitude, container });
        
        // Ensure container has proper constraints
        container.style.width = '100%';
        container.style.position = 'relative';
        container.style.overflow = 'hidden';

        try {
            // Create map instance
            this.map = L.map(container, {
                center: [this.latitude, this.longitude],
                zoom: this.zoom,
                scrollWheelZoom: true,
                zoomControl: true,
                attributionControl: true,
                preferCanvas: false
            });

            console.log('Map instance created:', this.map);

            // Add OpenStreetMap tile layer
            const tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                maxZoom: 19,
                minZoom: 1
            });
            
            tileLayer.addTo(this.map);
            console.log('Tile layer added');

            // Create custom icon
            const customIcon = L.icon({
                iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2VmNDQ0NCIgd2lkdGg9IjM2cHgiIGhlaWdodD0iMzZweCI+PHBhdGggZD0iTTEyIDJDOC4xMyAyIDUgNS4xMyA1IDljMCA1LjI1IDcgMTMgNyAxM3M3LTcuNzUgNy0xM2MwLTMuODctMy4xMy03LTctN3ptMCA5LjVjLTEuMzggMC0yLjUtMS4xMi0yLjUtMi41czEuMTItMi41IDIuNS0yLjUgMi41IDEuMTIgMi41IDIuNS0xLjEyIDIuNS0yLjUgMi41eiIvPjwvc3ZnPg==',
                iconSize: [36, 36],
                iconAnchor: [18, 36],
                popupAnchor: [0, -36]
            });

            // Add marker
            const marker = L.marker([this.latitude, this.longitude], { icon: customIcon }).addTo(this.map);
            console.log('Marker added');

            // Add popup with location info
            if (this.city && this.country) {
                marker.bindPopup(`
                    <div style="text-align: center; padding: 4px;">
                        <strong style="font-size: 14px;">${this.city}, ${this.country}</strong><br>
                        <span style="font-size: 12px; color: #666;">${this.latitude.toFixed(4)}, ${this.longitude.toFixed(4)}</span>
                    </div>
                `).openPopup();
            }

            // Fix tile rendering issues with multiple invalidateSize calls
            setTimeout(() => {
                if (this.map) {
                    this.map.invalidateSize(true);
                    console.log('Map size invalidated (100ms)');
                }
            }, 100);

            setTimeout(() => {
                if (this.map) {
                    this.map.invalidateSize(true);
                    console.log('Map size invalidated (500ms)');
                }
            }, 500);

            // Setup resize observer to handle container size changes
            this.resizeObserver = new ResizeObserver(() => {
                if (this.map) {
                    this.map.invalidateSize(true);
                }
            });
            this.resizeObserver.observe(container);

        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }
}
